import pushid from 'pushid';
import cloneDeep from 'clone-deep';
import router from '@/router/router';
import { toArray, success } from '@/helpers/utils';
import { EventBus } from '@/helpers/event-bus';

export const List = {
    state: {},
    mutations: {},
    getters: {
        getActiveListitemId(state, getters, rootState) {
            if (rootState.route.query.listitem_id) {
                return rootState.route.query.listitem_id;
            }
            return null;
        },
        getActiveListitem(state, getters, rootState) {
            if (
                rootState.route.query.module_id !== undefined &&
                rootState.route.query.listitem_id !== undefined
            ) {
                let prop = getters.getProp(
                    'tecpage.user.modules.' +
                        rootState.route.query.module_id +
                        '.' +
                        rootState.route.query.listitem_source +
                        '.' +
                        rootState.route.query.listitem_id
                );
                if (prop === undefined) {
                    return null;
                }
                return prop;
            }
            return null;
        },
        isListeditorActive(state, getters, rootState) {
            if (rootState.route.query.listeditor) {
                return true;
            }
            return false;
        },
        getListeditorSubpage(state, getters, rootState) {
            if (rootState.route.query.listeditor_subpage) {
                return rootState.route.query.listeditor_subpage;
            }
            return null;
        },
        isListeditorDirectEdited(state, getters, rootState) {
            if (
                rootState.route.query.listeditor_directedit &&
                rootState.route.query.listeditor_directedit == '1'
            ) {
                return true;
            }
            return false;
        },
        isListItemDeletable(state, getters) {
            return ({ id, source }) => {
                let item = getters.getProp(source + '.' + id);
                if (item === undefined) {
                    return false;
                }
                if ('preset_id' in item) {
                    return false;
                }
                return true;
            };
        },
        getListeditorSource(state, getters, rootState) {
            if (rootState.route.query.listeditor_source) {
                return rootState.route.query.listeditor_source;
            }
            return null;
        },
        getListItems(state, getters) {
            return (source, show_invisible = false, conditions = undefined) => {
                let items = toArray(getters.getProp(source));
                if (show_invisible === false) {
                    items = items.filter((i) => i.visible);
                }
                if (conditions !== undefined) {
                    Object.entries(conditions).forEach(([conditions__key, conditions__value]) => {
                        items = items.filter((i) => i[conditions__key] === conditions__value);
                    });
                }
                items.sort((a, b) => {
                    return a.pos - b.pos;
                });
                return items;
            };
        }
    },
    actions: {
        async initOrUpdateList(context, payload) {
            let source = context.getters.getProp(payload.dataSource) || {};

            // copy preset into data
            if (payload.dataPreset) {
                let preset = context.getters.getProp(payload.dataPreset) || {},
                    source_preset_ids = Object.keys(source).map((key) => source[key].preset_id),
                    preset_ids = Object.keys(preset).map((key) => preset[key].id);

                // remove entries from data if not present in preset
                source = Object.keys(source)
                    .filter((i) => !source[i].preset_id || preset_ids.includes(source[i].preset_id))
                    .reduce((res, key) => ((res[key] = source[key]), res), {});

                for (let preset__value of Object.values(preset)) {
                    // fetch data and convert if function is provided to object
                    let data = cloneDeep(preset__value.data);
                    if (typeof data === 'function') {
                        data = data(context);
                    }
                    // apply creation trigger if available
                    let dataOnCreate = cloneDeep(data);
                    if ('afterCreation' in preset__value) {
                        let dataOnCreateAdded = await preset__value.afterCreation(context);
                        dataOnCreate = {
                            ...dataOnCreate,
                            ...dataOnCreateAdded
                        };
                    }
                    // add entries to data if not yet available
                    if (!source_preset_ids.includes(preset__value.id)) {
                        let newId = pushid();
                        source[newId] = {
                            id: newId,
                            pos: preset__value.pos,
                            visible:
                                'default' in payload && payload.default === 'all' ? true : false,
                            data: dataOnCreate,
                            preset_id: preset__value.id
                        };
                    }
                    // update shadowed data
                    let elIndex = Object.keys(source).find(
                        (i) => source[i].preset_id === preset__value.id
                    );
                    if (elIndex) {
                        Object.entries(data).forEach(([data__key, data__value]) => {
                            source[elIndex].data[data__key] = data__value;
                        });
                    }
                }
            }

            // save list if preset available or on initial creation
            if (payload.dataPreset || Object.keys(source).length === 0) {
                await context.dispatch('setProp', {
                    source: payload.dataSource,
                    value: source
                });
            }
        },
        async createListItem(context, payload) {
            let newId = pushid(),
                newItem = {
                    id: newId,
                    pos: undefined,
                    visible: true
                };
            await context.dispatch('addProp', {
                source: payload.dataSource,
                value: newItem
            });
            await context.dispatch('routeAddQuery', {
                listeditor: 1,
                listeditor_subpage: payload.listSubpage,
                listeditor_source: payload.dataSource,
                listitem_id: newId
            });
            context.dispatch('addDifferCreatedListItem', {
                id: newId,
                source: payload.dataSource,
                route: context.getters.getCurrentRouteFullPath
            });
            return newId;
        },
        async removeListItem(context, { source, id, confirm, save, route }) {
            if (confirm === true) {
                await context.dispatch('routeRemoveQuery', [
                    'textpicker',
                    'textpicker_namespace',
                    'filemanager',
                    'filemanager_source',
                    'filemanager_accept',
                    'filemanager_multiple',
                    'fileeditor',
                    'fileeditor_file_id'
                ]);
                EventBus.$emit('controlhead-remove-open', context.getters.getCurrentRouteFullPath);
                EventBus.$once('controlhead-remove-accepted', async () => {
                    await context.dispatch('removeListItem', {
                        source: source,
                        id: id,
                        confirm: false,
                        save: save,
                        route: route
                    });
                });
                EventBus.$once('controlhead-remove-denied', () => {});
            } else {
                if (route === true) {
                    /* clear differ for route */
                    await context.dispatch(
                        'forgetDifferInRoute',
                        context.getters.getCurrentRouteFullPath
                    );
                    await context.dispatch('routeRemoveQuery', [
                        'listeditor',
                        'listeditor_subpage',
                        'listeditor_source',
                        'listeditor_directedit',
                        'listitem_id',
                        'textpicker',
                        'textpicker_namespace',
                        'filemanager',
                        'filemanager_source',
                        'filemanager_accept',
                        'filemanager_multiple',
                        'fileeditor',
                        'fileeditor_file_id'
                    ]);
                    /* if page was deleted, redirect to home */
                    if (source.indexOf('pages') > -1) {
                        await router.push({
                            name: 'editor',
                            params: {
                                page: ''
                            },
                            query: context.getters.getCurrentRouteQuery
                        });
                    }
                }
                await context.dispatch('removeProp', {
                    source: source + '.' + id
                });
                if (save === true) {
                    await context.dispatch('saveAllData');
                }
                success('Erfolgreich gelöscht');
            }
        },
        async directEditListeditor(context, { dataSource, listSubpage, module_id, listitem_id }) {
            await context.dispatch('routeAddQuery', {
                configurator: 1,
                configurator_group: 'modules',
                configurator_view: 'data',
                module_id: module_id,
                listeditor: 1,
                listeditor_subpage: listSubpage,
                listeditor_source: dataSource,
                listeditor_directedit: 1,
                listitem_id: listitem_id
            });
        }
    }
};
